import { useEffect, useMemo, useState } from "react";
import { useLocation, useMatches } from "@remix-run/react";
import {
  FEATURE_TOGGLE_EVENT,
  getFeatureToggleState,
  isPathInScope,
} from "~/lib/featureToggle";
import type {
  FeatureToggleEventDetail,
  FeatureToggleState,
} from "~/lib/featureToggle";

/** Breadcrumb paths of the category being rendered, empty off a category page. */
const useAncestorPaths = (): string[] => {
  const matches = useMatches();

  return useMemo(() => {
    for (const match of matches) {
      const breadcrumbs = (match.data as { result?: { categoryBreadcrumbs?: { path?: string }[] } })
        ?.result?.categoryBreadcrumbs;
      if (Array.isArray(breadcrumbs)) {
        return breadcrumbs
          .map((breadcrumb) => breadcrumb?.path)
          .filter((path): path is string => typeof path === "string" && path !== "");
      }
    }
    return [];
  }, [matches]);
};

/**
 * Subscribes to a client-side feature toggle.
 *
 * - During SSR and initial client render, returns `defaultEnabled`
 *   so the hydrated DOM matches the server output (no hydration mismatch).
 *
 * - After mount, reads the current override (in-memory or persisted) and
 *   applies it if present.
 *
 * - Re-renders whenever `window.wovarFeatureToggle(name, ...)` is called.
 *
 * - Re-checks the override's page scope on every navigation, so a toggle set
 *   with `paths` stops applying when the visitor moves to a page outside it
 *   and applies again when they come back (WOV-2195).
 *
 * NOTE: `name` must be a stable string reference across renders,
 * pass a module-level constant rather than an inline template literal
 * or computed value.
 *
 * The hook's effect re-subscribes whenever `name` changes, and a fresh-identity
 * name every render would thrash the listener and could drop events fired
 * in the detach/reattach gap.
 */
export const useFeatureToggle = (
  name: string,
  defaultEnabled = false
): boolean => {
  const [state, setState] = useState<FeatureToggleState | undefined>(undefined);
  const { pathname } = useLocation();
  const ancestorPaths = useAncestorPaths();

  useEffect(() => {
    // Attach the listener before reading the current value
    // so any toggle set between the read and the subscription can't be missed.
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<FeatureToggleEventDetail>).detail;
      if (detail?.name === name) {
        setState({
          enabled: detail.enabled,
          paths: detail.paths,
          includeChildren: detail.includeChildren,
        });
      }
    };
    window.addEventListener(FEATURE_TOGGLE_EVENT, handler);

    const stored = getFeatureToggleState(name);
    if (stored !== undefined) {
      setState(stored);
    }

    return () => window.removeEventListener(FEATURE_TOGGLE_EVENT, handler);
  }, [name]);

  if (state === undefined) {
    return defaultEnabled;
  }

  return (
    state.enabled &&
    isPathInScope(state.paths, pathname, {
      includeChildren: state.includeChildren,
      ancestorPaths,
    })
  );
};
